<script setup lang="ts">
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  StopOutlined,
  SyncOutlined,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import { onMounted, ref } from 'vue';
import { search } from '~/api/generated/clients/managed-jobs/search';
import { useDrawerStore } from '~/stores/drawer';

interface JobItem {
  codigo: string;
  tipoText: string;
  responsavelNome: string | null;
  dataSolicitacao: string;
  dataConclusao: string | null;
  status: number;
  statusText: string;
}

interface JobListResponse {
  items: JobItem[];
  totalCount: number;
}

const drawerStore = useDrawerStore();

const loading = ref(false);
const data = ref<JobItem[]>([]);
const totalCount = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);
const sortBy = ref<string | null>('dataSolicitacao');
const sortOrder = ref<'asc' | 'desc'>('desc');

const columns = [
  {
    title: 'TIPO',
    dataIndex: 'tipoText',
    key: 'tipoText',
    width: 200,
  },
  {
    title: 'RESPONSAVEL',
    dataIndex: 'responsavelNome',
    key: 'responsavelNome',
    width: 150,
  },
  {
    title: 'STATUS',
    dataIndex: 'statusText',
    key: 'statusText',
    width: 120,
  },
  {
    title: 'SOLICITADO EM',
    dataIndex: 'dataSolicitacao',
    key: 'dataSolicitacao',
    sorter: true,
    defaultSortOrder: 'descend' as const,
    width: 150,
    customRender: ({ text }: { text: string }) => {
      return text ? dayjs(text).format('DD/MM/YYYY HH:mm:ss') : '-';
    },
  },
  {
    title: 'CONCLUIDO EM',
    dataIndex: 'dataConclusao',
    key: 'dataConclusao',
    sorter: true,
    width: 150,
    customRender: ({ text }: { text: string | null }) => {
      return text ? dayjs(text).format('DD/MM/YYYY HH:mm:ss') : '-';
    },
  },
];

async function fetchData() {
  loading.value = true;
  try {
    const skip = (currentPage.value - 1) * pageSize.value;
    const backendSortBy =
      sortBy.value === 'dataSolicitacao' ? 'dataCriacao' : sortBy.value;
    const response = (await search({
      params: {
        skip,
        take: pageSize.value,
        tipo: 64, // TipoJobEnum.NovoJob
        actionKey: 'ImportarAtualizacoesGcpjAction',
        sortBy: backendSortBy,
        sortOrder: sortOrder.value,
      },
    })) as unknown as JobListResponse;
    data.value = response.items;
    totalCount.value = response.totalCount;
  } catch {
    data.value = [];
    totalCount.value = 0;
  } finally {
    loading.value = false;
  }
}

function onTableChange(_pagination: any, _filters: any, sorter: any) {
  if (sorter?.field) {
    sortBy.value = sorter.field;
    sortOrder.value = sorter.order === 'ascend' ? 'asc' : 'desc';
  } else {
    sortBy.value = 'dataSolicitacao';
    sortOrder.value = 'desc';
  }
  currentPage.value = 1;
  fetchData();
}

function abrirJob(jobId: string) {
  drawerStore.push2({
    componentName: 'AtualizacoesGcpjProgressDrawer',
    params: { jobId },
    onClose: () => {
      fetchData();
    },
  });
}

onMounted(() => {
  fetchData();
});

defineExpose({ fetchData });
</script>

<template>
  <div>
    <a-row :gutter="8" style="margin-bottom: 16px" type="flex">
      <a-col flex="auto" />
      <a-col flex="50px">
        <a-button size="large" :loading="loading" @click="fetchData">
          <template #icon>
            <ReloadOutlined />
          </template>
          Recarregar
        </a-button>
      </a-col>
    </a-row>

    <a-table
      class="graphql-table"
      :columns="columns"
      :data-source="data"
      :loading="loading"
      :bordered="true"
      :pagination="false"
      row-key="codigo"
      @change="onTableChange"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'tipoText'">
          <a style="text-transform: uppercase" @click="abrirJob(record.codigo)"
            >Importar atualizações de GCPJ</a
          >
        </template>
        <template v-if="column.key === 'statusText'">
          <span style="text-transform: uppercase">
            <CheckCircleOutlined
              v-if="record.statusText?.toLowerCase() === 'sucesso'"
              style="color: #52c41a; margin-right: 6px"
            />
            <CloseCircleOutlined
              v-else-if="record.statusText?.toLowerCase() === 'falha'"
              style="color: #ff4d4f; margin-right: 6px"
            />
            <SyncOutlined
              v-else-if="record.statusText?.toLowerCase() === 'processando'"
              spin
              style="color: #1890ff; margin-right: 6px"
            />
            <SyncOutlined
              v-else-if="record.statusText?.toLowerCase() === 'em cancelamento'"
              spin
              style="color: #faad14; margin-right: 6px"
            />
            <StopOutlined
              v-else-if="record.statusText?.toLowerCase() === 'cancelado'"
              style="color: #faad14; margin-right: 6px"
            />
            <ClockCircleOutlined
              v-else-if="record.statusText?.toLowerCase() === 'enfileirado'"
              style="color: #1890ff; margin-right: 6px"
            />
            {{ record.statusText }}
          </span>
        </template>
      </template>

      <template #footer>
        <span class="table-resultado-span">Resultados: {{ totalCount }}</span>
      </template>
    </a-table>

    <a-row>
      <a-col
        :span="24"
        style="text-align: right; padding-right: 16px; padding-top: 16px"
      >
        <a-pagination
          :current="currentPage"
          :page-size="pageSize"
          :disabled="loading"
          :show-size-changer="true"
          :page-size-options="['10', '20', '30', '40', '50']"
          :total="totalCount"
          @change="
            (page: number, size: number) => {
              currentPage = page;
              pageSize = size;
              fetchData();
            }
          "
        />
      </a-col>
    </a-row>
  </div>
</template>
